import React, { useState, useEffect, useContext } from "react"
import {
  Button,
  Col,
  Collapse,
  Form,
  Input,
  Label,
  Row,
  UncontrolledAlert,
  Card,
  CardBody,
  Modal,
} from "reactstrap"
import TenantConnectionContext from "./TenantConnectionContext"
import LoadingOverlay from "react-loading-overlay"
import Error from "pages/NotificationModal/Error"
import Success from "./Success"
import Confirmation from "./Confirmation"
import SecretInput from "./SecretInput"

const METHOD_OPTIONS = [
  { value: "CustomerBasic", label: "Customer Basic (username + password)" },
  { value: "CustomerToken", label: "Customer Token (username + token)" },
  { value: "BearerToken", label: "Bearer Token" },
  { value: "ApiToken", label: "API Token (x-api-key header)" },
  { value: "OAuthToken", label: "OAuth 2.0 — client credentials" },
  {
    value: "subscriptionToken",
    label: "OAuth 2.0 + Subscription Key (Azure APIM)",
  },
  { value: "OAuth1", label: "OAuth 1.0 (RFC 5849 signing)" },
  { value: "token_name", label: "Custom Header" },
]

// credential fields stored in table columns, keyed by method
const COLUMN_FIELDS = {
  CustomerBasic: [
    { name: "username", label: "Username" },
    { name: "password", label: "Password", secret: true },
  ],
  CustomerToken: [
    { name: "username", label: "Username" },
    { name: "token", label: "Token", secret: true },
  ],
  BearerToken: [{ name: "token", label: "Token", secret: true }],
  ApiToken: [{ name: "token", label: "Token", secret: true }],
  token_name: [{ name: "token", label: "Token", secret: true }],
}

// credential fields stored inside config.<group>, keyed by method
const AUTH_GROUPS = {
  OAuthToken: {
    group: "oauth",
    fields: [
      { name: "token_url", label: "Token URL" },
      { name: "clientId", label: "Client ID" },
      { name: "clientSecret", label: "Client Secret", secret: true },
      { name: "scope", label: "Scope" },
    ],
  },
  subscriptionToken: {
    group: "subscription",
    fields: [
      { name: "token_url", label: "Token URL" },
      { name: "clientId", label: "Client ID" },
      { name: "clientSecret", label: "Client Secret", secret: true },
      { name: "subscriptionKey", label: "Subscription Key", secret: true },
    ],
  },
  OAuth1: {
    group: "oauth1",
    fields: [
      { name: "consumer_key", label: "Consumer Key" },
      { name: "consumer_secret", label: "Consumer Secret", secret: true },
      { name: "token", label: "Token", secret: true },
      { name: "token_secret", label: "Token Secret", secret: true },
      {
        name: "signature_method",
        label: "Signature Method",
        type: "select",
        options: ["HMAC-SHA1", "HMAC-SHA256"],
      },
      { name: "realm", label: "Realm (optional)" },
    ],
  },
}

// Outbound appointment status codes that can have a per-status override URL
// (config.sts_urls). Matches the set the v2 outbound Lambda emits.
const STATUS_CODES = [
  { code: "QPND", label: "QPND — Pending" },
  { code: "QREQ", label: "QREQ — Requested" },
  { code: "QCON", label: "QCON — Confirmed" },
  { code: "QISU", label: "QISU — Exception" },
]

// group name (in AUTH_GROUPS) -> config key holding that group's secrets, used to
// build the reveal path (<config_key>.<field>) the backend expects.
const GROUP_CONFIG_KEY = {
  oauth: "oauth_config",
  subscription: "subscription_config",
  oauth1: "oauth1_config",
}

function columnFieldsFor(method) {
  return COLUMN_FIELDS[method] || []
}

const TenantConnectionForm = ({ hideForm }) => {
  const {
    loading,
    success,
    error,
    errorMessage,
    setSuccess,
    setError,
    tenantData,
    submitUpdate,
    revealSecret,
    updateResponse,
  } = useContext(TenantConnectionContext)

  const [method, setMethod] = useState("")
  const [headerName, setHeaderName] = useState("")
  const [common, setCommon] = useState({ url: "" })
  const [cols, setCols] = useState({ username: "", password: "", token: "" })
  const [authFields, setAuthFields] = useState({})
  const [advHeaders, setAdvHeaders] = useState("")
  const [stsUrls, setStsUrls] = useState({})
  const [showAdvanced, setShowAdvanced] = useState(false)
  const [errorMsg, setErrorMsg] = useState("")
  const [showConfirmation, setShowConfirmation] = useState(false)

  useEffect(() => {
    if (!tenantData) return
    const uiMethod = tenantData.ui_method || tenantData.method || ""
    setMethod(uiMethod)
    setHeaderName(tenantData.header_name || "")
    setCommon({ url: tenantData.url || "" })
    setCols({
      username: tenantData.username || "",
      password: tenantData.password || "",
      token: tenantData.token || "",
    })
    const grp = AUTH_GROUPS[uiMethod] ? AUTH_GROUPS[uiMethod].group : null
    setAuthFields(grp ? { ...((tenantData.auth || {})[grp] || {}) } : {})
    const adv = tenantData.advanced || {}
    setAdvHeaders(adv.headers ? JSON.stringify(adv.headers, null, 2) : "")
    setStsUrls(adv.sts_urls || {})
  }, [tenantData])

  const handleMethodChange = e => {
    const newMethod = e.target.value
    setMethod(newMethod)
    const grp = AUTH_GROUPS[newMethod] ? AUTH_GROUPS[newMethod].group : null
    const stored = grp && tenantData?.auth ? tenantData.auth[grp] : null
    setAuthFields(grp ? { ...(stored || {}) } : {})
  }

  const setCol = (name, value) => setCols(prev => ({ ...prev, [name]: value }))
  const setAuthField = (name, value) =>
    setAuthFields(prev => ({ ...prev, [name]: value }))

  const renderField = (field, value, onChange, keyPrefix) => {
    if (field.type === "select") {
      return (
        <Input
          type="select"
          value={value || ""}
          onChange={e => onChange(field.name, e.target.value)}
        >
          <option value="">Select</option>
          {field.options.map(opt => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </Input>
      )
    }
    if (field.secret) {
      const revealPath =
        keyPrefix === "col"
          ? field.name
          : `${GROUP_CONFIG_KEY[keyPrefix] || keyPrefix}.${field.name}`
      return (
        <SecretInput
          value={value}
          onChange={v => onChange(field.name, v)}
          label={field.label}
          revealPath={revealPath}
          onReveal={revealSecret}
        />
      )
    }
    return (
      <Input
        type="text"
        value={value || ""}
        onChange={e => onChange(field.name, e.target.value)}
        placeholder={`Enter ${field.label.toLowerCase()}`}
      />
    )
  }

  const fieldRow = (label, node) => (
    <Row className="mb-2" key={label}>
      <Label className="col-sm-3 col-form-label">{label}</Label>
      <Col sm={6} className="ms-4">
        {node}
      </Col>
    </Row>
  )

  const handleSubmit = () => {
    setErrorMsg("")
    if (!method) {
      setErrorMsg("Please select an auth method")
      return
    }
    if (method === "token_name" && !headerName.trim()) {
      setErrorMsg("Header name is required for Custom Header")
      return
    }
    setShowConfirmation(true)
  }

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false)
    const payload = {
      method,
      url: common.url,
      auth: {},
    }
    if (method === "token_name") {
      payload.header_name = headerName
    }
    columnFieldsFor(method).forEach(f => {
      payload[f.name] = cols[f.name]
    })
    const grpDef = AUTH_GROUPS[method]
    if (grpDef) {
      payload.auth[grpDef.group] = { ...authFields }
    }

    const advanced = {}
    const prevAdv = tenantData.advanced || {}
    try {
      if (advHeaders.trim()) advanced.headers = JSON.parse(advHeaders)
      else if (prevAdv.headers) advanced.headers = ""
    } catch (e) {
      setErrorMsg("Extra Headers must be valid JSON")
      return
    }
    const cleanedSts = {}
    Object.entries(stsUrls).forEach(([code, val]) => {
      if (val && String(val).trim()) cleanedSts[code] = String(val).trim()
    })
    if (Object.keys(cleanedSts).length) advanced.sts_urls = cleanedSts
    else if (prevAdv.sts_urls) advanced.sts_urls = ""
    if (Object.keys(advanced).length) payload.advanced = advanced

    await submitUpdate(payload)
  }

  const handleCancel = () => {
    setShowConfirmation(false)
    hideForm()
  }
  const closeSuccess = () => {
    setSuccess(false)
    hideForm()
  }
  const togError = () => setError(false)

  if (!tenantData) {
    return (
      <LoadingOverlay active={loading} spinner>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h5>Loading...</h5>
        </div>
      </LoadingOverlay>
    )
  }

  const grpDef = AUTH_GROUPS[method]

  return (
    <>
      <Modal isOpen={success} toggle={closeSuccess} centered size="md">
        <Success data={updateResponse} cancel={closeSuccess} />
      </Modal>
      <Modal isOpen={error} toggle={togError} centered size="sm">
        <Error
          message={errorMessage || "Something went wrong"}
          closeModal={togError}
        />
      </Modal>
      <Modal
        isOpen={showConfirmation}
        toggle={() => setShowConfirmation(false)}
        centered
        size="sm"
      >
        <Confirmation
          message="Do you want to save these changes?"
          confirm={handleConfirmSubmit}
          cancel={() => setShowConfirmation(false)}
        />
      </Modal>

      <LoadingOverlay active={loading} spinner>
        <Card className="p-3">
          <div className="mb-3">
            <h6 className="text-primary">Edit Tenant Inbound Integration</h6>
          </div>
          <CardBody className="p-0">
            <Card outline color="primary" className="border mt-2 mb-3">
              <CardBody className="p-3">
                {errorMsg && (
                  <div className="mb-3">
                    <UncontrolledAlert color="danger" role="alert">
                      {errorMsg}
                    </UncontrolledAlert>
                  </div>
                )}
                <Form>
                  {fieldRow(
                    "URL",
                    <Input
                      type="text"
                      value={common.url}
                      placeholder="Enter url"
                      onChange={e =>
                        setCommon(p => ({ ...p, url: e.target.value }))
                      }
                    />
                  )}
                  {fieldRow(
                    "Auth Method *",
                    <Input
                      type="select"
                      value={method}
                      onChange={handleMethodChange}
                    >
                      <option value="">Select method</option>
                      {METHOD_OPTIONS.map(m => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Input>
                  )}

                  {method === "token_name" &&
                    fieldRow(
                      "Header Name *",
                      <Input
                        type="text"
                        value={headerName}
                        placeholder="e.g. Api-Key"
                        onChange={e => setHeaderName(e.target.value)}
                      />
                    )}

                  {columnFieldsFor(method).map(f =>
                    fieldRow(
                      f.label,
                      renderField(f, cols[f.name], setCol, "col")
                    )
                  )}

                  {grpDef &&
                    grpDef.fields.map(f =>
                      fieldRow(
                        f.label,
                        renderField(
                          f,
                          authFields[f.name],
                          setAuthField,
                          grpDef.group
                        )
                      )
                    )}

                  <Row className="mb-2">
                    <Col sm={9} className="ms-4">
                      <Button
                        color="link"
                        className="p-0"
                        onClick={() => setShowAdvanced(s => !s)}
                      >
                        {showAdvanced ? "▾" : "▸"} Advanced (extra headers /
                        per-status URLs)
                      </Button>
                    </Col>
                  </Row>
                  <Collapse isOpen={showAdvanced}>
                    {fieldRow(
                      "Extra Headers (JSON)",
                      <Input
                        type="textarea"
                        rows="3"
                        value={advHeaders}
                        placeholder='{"X-Custom": "value"}'
                        style={{ fontFamily: "monospace", fontSize: "13px" }}
                        onChange={e => setAdvHeaders(e.target.value)}
                      />
                    )}
                    <Row className="mb-1">
                      <Col sm={9} className="ms-4">
                        <small className="text-muted">
                          Per-status URLs (optional) — override the endpoint for
                          specific appointment statuses. Leave blank to use the
                          main URL above.
                        </small>
                      </Col>
                    </Row>
                    {STATUS_CODES.map(s =>
                      fieldRow(
                        s.label,
                        <Input
                          type="text"
                          value={stsUrls[s.code] || ""}
                          placeholder={`https://… (${s.code})`}
                          onChange={e =>
                            setStsUrls(prev => ({
                              ...prev,
                              [s.code]: e.target.value,
                            }))
                          }
                        />
                      )
                    )}
                  </Collapse>
                </Form>
              </CardBody>
            </Card>

            <div className="d-flex justify-content-end mt-3 me-4">
              <div className="d-flex gap-2">
                <Button onClick={handleSubmit} className="btn" color="primary">
                  Save
                </Button>
                <Button
                  onClick={handleCancel}
                  className="btn"
                  color="secondary"
                >
                  Cancel
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
      </LoadingOverlay>
    </>
  )
}

export default TenantConnectionForm
